import React, { useState, useEffect, useLayoutEffect, useMemo, useReducer, useCallback, useRef } from "react";
import Head from "next/head";
import dynamic from 'next/dynamic';
import ChannelSearch from '../video-lab/ChannelSearch';
import {
  chlupStoreReducer, initializeChlupStore,
  filterCategoricalChannels,
  reconcileSearchSelections,
  type SearchChoice
} from "./ChlupStore";
import { ChnlListingPageFeed } from "./chnlListingPage.props";
import {
  KeyValuePair,
  Choice,
  PACKAGE_INDICATORS
} from "@directv-digital/dds.services.channel-lineup-provider";
import {
  getDmaCookies
} from "@directv-digital/dds.services.cookie-service";
// Generated by Copilot- Start - 2026-04-01 20:31:38
import { useGlobalConfigs } from "@directv-digital/dds.server.application-context";
// Generated by Copilot- End - 2026-04-01 20:31:38
import { zIndexMap } from "./utils/zIndexMap";
import { buildLocalRsnChannel, isChannelInGenre, isNationalForGenre } from "./utils/utilityFunctions";
import { LOCAL_DEFAULT_PACKAGES, RSN_DEFAULT_PACKAGES } from "./utils/fallbackData";
import { getSignaturePackagesForLocalChannel, signatureExclusionsForSurface } from "lib/service/channels/get-local-national-dma-eligible-channels";
import { County } from "@directv-digital/dds.apis.county-lookup";
import { LocalRsnApiResponse } from "@directv-digital/dds.apis.local-rsn-chnls";

import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvModal from '@directv-digital/dds.ui.dtv-modal';
import DtvText from "@directv-digital/dds.ui.dtv-text";
import ErrorDisplay from "@directv-digital/dds.ui.error-display";
import { colorPalette } from "@directv-digital/dds.ui.dtv-theme";

/** Components (Above fold) ---------------------------- */
import { PackageComparisonGrid } from './PackageComparisonGrid';
import ZipcodeModule from "src/components/video-lab/zipcode-module";
import { IconBanner } from "./IconBanner";
import MyFreeDtvBanner from "./components/MyFreeDtvBanner";
import TabTray from "src/components/video-lab/tab-tray";

/** Below Fold components
 * - rendered per condition not on load
 * - low priority
 * */
const ChnlListingPageFooter = dynamic(() => import("./components/Footer"), { ssr: false });
const ScrollToTop = dynamic(() => import('@directv-digital/dds.ui.scroll-to-top'), { ssr: false });
const RsnChannelModal = dynamic(() => import("./components/RsnChannelModal"), { ssr: false });
const ChannelModal = dynamic(() => import("./components/ChannelModal"), { ssr: false });

function safeRequestIdleCallback(callback, timeout?: number) {
  if (typeof requestIdleCallback === "function") {
    requestIdleCallback(callback, { timeout: 2000 });
  } else {
    setTimeout(callback, timeout || 0);
  }
}

// useLayoutEffect on client (fires before paint), useEffect on server (never runs during SSR).
// Avoids React's "useLayoutEffect does nothing on the server" warning.
const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

export function ChnlListingPage(props: ChnlListingPageFeed) {
  try {
    const componentId = props?.options?.id || "Chnl-Listing-Page";
    const {
      channelData,
      config,
      content,
      searchObj
    } = props;
    const {
      theming,
      genrePackTriggers = [],
      heading,
      toggleBtn,
      subheading,
      subheadingLegal,
      zipcodeModuleConfig,
      myFreeDirectvBanner,
      searchConfig,
      pkgIndicatorIcons,
      gridInitViewMode,
      gridSortOptions,
      pkgIndicatorTooltipPrefixText,
      addOnPromoLogoInfo,
      categoryFooterCtas,
      internetChnlsPDFLink,
      satelliteChnlsPDFLink,
      satelliteCta,
      channelModalContent,
      localChnlBadgeTooltip,
      rsnChnlBadgeTooltip,
      subscriptionsLabel,
      channelsLabel,
      embeddedSvods,
      isEmbeddedSvodsEnabled,
      genreNationals,
      myFreeConfig,
      isSatelliteChlup,
      pageHeading,
      genreMapping,
      genreToPackageMapping
    } = content;
    const {
      channels,
      bannerCategories,
      bannerPackages,
      searchCategories: _searchCategories,
      gridCategories,
      gridPackages,
      myFreeDtvChannels = []
    } = channelData;
    // Generated by Copilot- Start - 2026-04-01 20:31:38
    const globalConfigs = useGlobalConfigs();
    const { genreLocalChannelMapping } = globalConfigs ?? {};
    // Generated by Copilot- End - 2026-04-01 20:31:38
    const chnlSrcLC = config?.chnlSrc?.toLowerCase();
    const isSatQP = config?.chnlSrc?.toLowerCase() === "satelliteclup";
    // [WAT-11768] `signatureExcludedLocalChannels` is a STREAM-only rule. This is the page's only
    // read of it, so gating here covers every downstream consumer (grid init, local injection at
    // getSignaturePackagesForLocalChannel, and the store's per-package filter). Both satellite
    // signals are OR'd on purpose: either one alone identifies the satellite mount, and missing it
    // hides a real local affiliate — the failure this ticket exists to stop.
    const signatureExcludedLocalChannels = signatureExclusionsForSurface(
      globalConfigs?.signatureExcludedLocalChannels,
      Boolean(isSatelliteChlup) || isSatQP
    );
    const localRsnApiOptions = useMemo(() => ({
      satellite: isSatQP,
      stream: !isSatQP
    }), []);

    /** ------------------------------------------------------------------------------- */
    /** Toggle Button state ------------------------------------------------------------*/
    // !!!!!Must be first thing done
    // --> toggle state directly affects which views channel set to compute/display first

    // Chlup Default Tab to Genre if user comes from genre specific URL's
    // [WAT-11911] `findIndex` returns -1 when no toggle carries `selected`, and `-1 || 0` is
    // -1, not 0 — which then read `toggles[-1]?.label` as undefined and left the page with no
    // tab, no label and (before this ticket) no view at all. A tray we cannot read a selection
    // from falls back to the first tab, the same default the page has always shown.
    const selectedIndex = toggleBtn?.toggles?.findIndex(x => x?.selected) ?? -1;
    let defaultIndex = selectedIndex > -1 ? selectedIndex : 0;
    if (genrePackTriggers?.length) {
      genrePackTriggers?.some(triggerUrl => {
        if (typeof window !== "undefined" && document.referrer?.includes(triggerUrl)) {
          toggleBtn.toggles[0].selected = false;
          toggleBtn.toggles[1].selected = true;
          defaultIndex = 1;
          return true;
        }
        toggleBtn.toggles[0].selected = true;
        toggleBtn.toggles[1].selected = false;
        defaultIndex = 0;
        return false;
      });
    }
    if (config?.viewQueryParam === 'genre') {
      const genreIdx = toggleBtn?.toggles?.findIndex(t => t.label?.toLowerCase().includes('genre'));
      if (genreIdx > -1) {
        defaultIndex = genreIdx;
      }
    }

    const [selectedToggle, setSelectedToggle] =
    useState(isSatelliteChlup ? "PACKAGES" : toggleBtn?.toggles[defaultIndex]?.label?.toUpperCase())
    const [tabDefaultIndex, setTabDefaultIndex] = useState(defaultIndex);
    const [tabKey, setTabKey] = useState(0);
    const viewQPCorrectionApplied = useRef(false);
    // [WAT-11911] The current tab is tracked by POSITION, not by looking its label back up.
    // Two toggles can carry the same label — a CMS author renames either one — and a label
    // lookup returns the FIRST match, so after moving to the second tab the guard in
    // handleTabTrayBtnChange believed the first was current and swallowed the click back.
    const [curToggleIndex, setCurToggleIndex] = useState(defaultIndex);

    // viewQPCorrectionApplied.current is a run-once guard: without it, setSelectedToggle would change
    // selectedToggle, re-trigger this effect (selectedToggle is in deps), and loop infinitely.
    useIsomorphicLayoutEffect(() => {
      if (viewQPCorrectionApplied.current) {
        return;
      }
      const params = new URLSearchParams(window.location.search);
      const genreToggle = toggleBtn?.toggles?.find(t => t.label?.toLowerCase().includes('genre'));
      const genreToggleIdx = genreToggle ? toggleBtn.toggles.indexOf(genreToggle) : -1;
      if (params.get('view')?.toLowerCase() === 'genre' && genreToggle) {
        viewQPCorrectionApplied.current = true;
        const genreLabel = genreToggle.label?.toUpperCase();
        if (genreLabel && selectedToggle !== genreLabel) {
          setSelectedToggle(genreLabel);
          setCurToggleIndex(genreToggleIdx);
          setTabDefaultIndex(genreToggleIdx);
          setTabKey(k => k + 1);
          dispatchChlupState({
            type: "UPDATE_COMPONENT_VIEW",
            data: {
              selectedToggle: genreLabel,
              selectedToggleIndex: genreToggleIdx
            }
          });
        }
      }
    }, [toggleBtn, selectedToggle]);

    // [WAT-11911] `_handleToggleBtnChange` used to live here — a second, non-GP tab handler no
    // component was ever wired to (the tray uses handleTabTrayBtnChange below). Removed rather
    // than taught the new position rule: a dispatch path nothing calls cannot be verified, and
    // leaving it meant shipping a comment that documented live behaviour for an absent caller.

    // GP view toggle
    const handleTabTrayBtnChange = index => {
      if(index !== curToggleIndex) {
        setCurToggleIndex(index);
        setSelectedToggle(toggleBtn?.toggles?.[index]?.label?.toUpperCase());
        dispatchChlupState({
          type: "UPDATE_COMPONENT_VIEW",
          data: {
            selectedToggle: toggleBtn?.toggles?.[index]?.label?.toUpperCase(),
            selectedToggleIndex: index
          }
        })
      }
    }
    /** ------------------------------------------------------------------------------- */

    /** Channels store -----------------------------------------------------------------*/
    const gridCategoriesAZ = [
      {
        key: 'INCLUDED_CHANNELS',
        value: 'Included channels'
      },
      {
        key: 'ADD_ON_CHANNELS',
        value: 'Add-on channels'
      }
    ];
    // Add MyFree Category to A-Z View
    if(myFreeConfig?.myFreeCategory) {
      gridCategoriesAZ.push(myFreeConfig?.myFreeCategory);
    }
    // Add EmbeddedSVODs Accordian Data to A-Z View only if feature is enabled
    if (isEmbeddedSvodsEnabled) {
      gridCategoriesAZ.unshift({
        key: 'INCLUDED_SUBSCRIPTIONS',
        value: 'Included subscriptions'
      } );
    }

    const [chlupStore, dispatchChlupState] = useReducer(
      chlupStoreReducer,
      {
        channels,
        // Per-DMA signatureExcluded gating happens inside the store [WAT-11301]: blanket-blocked
        // channels are hidden everywhere; perDmaOverride exclusions apply only to the signature
        // grid (the genre banner keeps pdo-covered nationals, renamed via genre-nationals).
        signatureExcludedLocalChannels,
        embeddedSvods,
        // [WAT-11256 round-2 S5] Gates searchChoicesSvods in initializeChlupStore —
        // the SVOD chip must not be offered when the feature flag is off.
        isEmbeddedSvodsEnabled,
        selectedToggle,
        // [WAT-11911] Position of the tab that seeded selectedToggle. The store falls back
        // to it when the CMS display label is not one it recognises, so a tab rename can no
        // longer leave activeView undefined and blank the grid.
        selectedToggleIndex: defaultIndex,
        bannerCategories,
        bannerPackages,
        gridCategoriesAZ,
        gridCategories,
        gridPackages,
        myFreeKey: myFreeConfig?.myFreeCategory?.key,
        currentDma: getDmaCookies()?.currentDma || getDmaCookies()?.nielsenDma,
        genreNationalMapping: genreNationals?.genreNationalMapping,
        genreMapping
      },
      initializeChlupStore
    );
    const {
      cmprGridChannelsAZ,
      cmprGridChannelsNum,
      cmprGridChannelsCategorical,
      bannerChannels,
      searchChoices,
      needsUpdate,
      activeView,
      searchChoicesSvods
    } = chlupStore;
    /** State mutating the displayed channels */
    const [selectedCategories, setSelectedCategories] = useState<KeyValuePair[]>([]);
    // [WAT-11771] Typed as SearchChoice, not the bare Choice: the grid filter matches on
    // the `uuid` getSearchChoices stamps, and holding the selection as `Choice` let a
    // refactor drop it in transit with no compile error, silently degrading the match to
    // name-only — the state that returned an empty grid for an alias-renamed channel.
    const [searchChannelList, setSearchChannelList] = useState<SearchChoice[]>([]);
    // [WAT-11779] A ZIP change rebuilds `searchChoices` for the new DMA, but the SELECTION is
    // component state and used to survive that rebuild untouched. A local/RSN channel's `matchId`
    // is market-specific (buildLocalRsnChannel keys it on the beam feed's array index + that
    // market's tuner number), so the carried-over chip compared a dead identity and the page told
    // the customer "Channel is not available in Genre Packs" for a channel the new market carries.
    // Re-point the chips at the new market's rows here; see reconcileSearchSelections for why it
    // re-stamps rather than drops, and why an ambiguous name is left alone.
    //
    // Deliberately the FUNCTIONAL setState form: `searchChannelList` stays out of the dep array
    // (adding it would re-run this on every selection change), and the helper returns the same
    // array reference when nothing needed re-pointing, so React bails out instead of re-rendering
    // on every store update.
    useEffect(() => {
      setSearchChannelList(prev => reconcileSearchSelections(prev, searchChoices));
    }, [searchChoices]);
    const searchActive = useMemo(() => searchChannelList?.length > 0, [searchChannelList]);
    const [showMoreCategories, setShowMoreCategories] = useState([]);

    /** Local/Rsn API state ------------------------------------------------------------*/
    const [isLoadingLocalRsn, setIsLoadingLocalRsn] = useState(true);
    const [fetchedZipcodeModuleData, setFetchedZipcodeModuleData] = useState(null);

    /** -------------------------------------------------------------------------------
   *  Channel Data sets
   *  */
    const filteredGridChannelsAZ = useMemo(() => {
      if (searchChannelList?.length === 0 || Object.keys(cmprGridChannelsAZ)?.length === 0) {
        return cmprGridChannelsAZ;
      }
      return filterCategoricalChannels(
        { searchList: searchChannelList.filter(item => item.type !== "svod")  },
        cmprGridChannelsAZ
      );
    }, [cmprGridChannelsAZ, searchChannelList]);

    const filteredEmbeddedSvods = useMemo(() => {
      if (searchChannelList?.length === 0 || Object.keys(embeddedSvods)?.length === 0) {
        return embeddedSvods;
      }

      const searchTagsvalues = searchChannelList.filter(item => item.type === "svod").flatMap(item => item.value);
      const embeddedSvodArr = embeddedSvods.filter(item => searchTagsvalues.includes(item.name));
      return embeddedSvodArr
    }, [embeddedSvods, searchChannelList]);

    const filteredGridChannelsCategorical = useMemo(() => {
      if (searchChannelList?.length === 0 || Object.keys(cmprGridChannelsCategorical)?.length === 0) {
        return cmprGridChannelsCategorical;
      }
      return filterCategoricalChannels(
        { searchList: searchChannelList.filter(item => item.type !== "svod") },
        cmprGridChannelsCategorical
      )
    }, [cmprGridChannelsCategorical, searchChannelList]);

    const filteredGridChannelsNum = useMemo(() => {
      if (searchChannelList?.length === 0 || Object.keys(cmprGridChannelsNum)?.length === 0) {
        return cmprGridChannelsNum;
      }
      return filterCategoricalChannels(
        { searchList: searchChannelList.filter(item => item.type !== "svod") },
        cmprGridChannelsNum
      );
    }, [cmprGridChannelsNum, searchChannelList]);

    /** Icon Banner w/Genre = FALSE only */
    // ^^ Remove this comment if categories enabled for Packages Grid
    const bannerSelectedCategories = useMemo(() => bannerCategories, [selectedCategories]);

    const filteredBannerChannels = useMemo(() => {
      if (searchChannelList?.length === 0 || Object.keys(bannerChannels)?.length === 0) {
        return bannerChannels;
      }
      return filterCategoricalChannels(
        { searchList: searchChannelList },
        bannerChannels
      );
    }, [bannerChannels, searchChannelList]);

    // [WAT-11765] The banner has no linear row to hang a subscription-only search on:
    // filterCategoricalChannels drops every category whose channels all fail the match, so
    // filteredBannerChannels (the value passed down — the store's bannerChannels is unchanged)
    // comes back {} and IconBanner falls to the no-match copy. The banner's
    // subscription surface is the per-pack Subscriptions row (IconCategory, WAT-11225),
    // keyed off IPackage.svods — so the pack keys have to come from the SVOD choices, not
    // from the linear filter.
    const searchedSvodNames = useMemo(
      () => searchChannelList?.filter(item => item?.type === "svod")?.map(item => item?.value) || [],
      [searchChannelList]
    );
    // [WAT-11772] The offer codes of the searched subscriptions — the identity the banner
    // matches on. Kept as a separate memo rather than folded into searchedSvodNames because
    // the two are not 1:1: an entry authored without an offer code contributes a name and no
    // code, and the banner needs both lists to decide.
    // [WAT-11772] PAIRS, not a separate code list. See IconBannerProps.searchedSvods: two
    // independent lists let a row match on a code from one selection and a label from another.
    const searchedSvods = useMemo(
      () => searchChannelList
        ?.filter(item => item?.type === "svod")
        ?.map(item => ({
          offerCode: item?.offerCode,
          name: item?.value as string
        })) || [],
      [searchChannelList]
    );

    /** >> When chlupStore is initialized or local/rsn channels are added:
   *    - ONLY the current page's channel set(s) are calculated to avoid unnecessary computation (~5-20ms)
   *    - needsUpdate flag is set true for other page(s)
   *    - below useEffects is triggered
   *    - idle callbacks created to update when main thread open
   *    - on component view switch, (when main thread open) to update channel sets
   */
    useEffect(() => {
      if (chlupStore?.needsUpdate?.banner === true) {
        if (activeView === 'categories') {
          dispatchChlupState({ type: 'REFRESH_BANNER' })
        } else {
          safeRequestIdleCallback(() => dispatchChlupState({ type: 'REFRESH_BANNER' }), 50);
        }
      } else if (chlupStore?.needsUpdate?.grid === true) {
        if (activeView === 'packages') {
          dispatchChlupState({ type: 'REFRESH_PKGS_GRID' })
        } else {
          safeRequestIdleCallback(() => dispatchChlupState({ type: 'REFRESH_PKGS_GRID' }), 50);
        }
      }
    }, [needsUpdate, activeView]);

    /** -------------------------------------------------------------------------------
   *  Zipcode Module Callback functions
   *  */
    const onZipcodeUpdateClick = useCallback(
      (zipcode: string, counties: County[] | null, rsnFee?: string, localRsnChnls?: LocalRsnApiResponse | null, county?: County) => {
        setIsLoadingLocalRsn(true); // Set state for Icon Banner UI
        setFetchedZipcodeModuleData(() => ({
          zipcode,
          counties,
          rsnFee,
          localRsnChnls,
          county
        }))
      }
      , []
    );

    /** Builds data DIRECTLY from LocalRsnChnlService */
    const buildLocalRsnChannels = useCallback((channels: LocalRsnApiResponse, currentDma: string, nielsenDma: string) => {
      const { localChannels = [], rsnChannels = [] } = channels;
      if (localChannels?.length === 0 && rsnChannels?.length === 0) {
        console.info("[ChannelListingPage] buildLocalRsnChannels: local and rsn channels not found")
      }

      let localrsnUUID = 0;
      const genreMappingKeys = Object.keys(genreMapping);

      const tempLocalChannels = [];
      let tempRsnChannels = [];

      const getGenrePackages = channel => {
        const genrePackages = [];
        const isNational = {};
        for (const genre of genreMappingKeys) {
          if (isChannelInGenre(channel, genreLocalChannelMapping, genre, currentDma, nielsenDma, genreToPackageMapping[genre])) {
            genrePackages.push(genreToPackageMapping[genre]);
          } else if(isNationalForGenre(channel, genre, currentDma, nielsenDma, genreNationals?.genreNationalMapping)){
            isNational[genre] = true;
            // Also set isNational for the banner category key (package name without ATT_TV_ prefix, lowercased)
            const packageName = genreToPackageMapping[genre];
            const bannerKey = packageName?.replace(/^ATT_TV_/i, '')?.toLowerCase();
            if (bannerKey && bannerKey !== genre) {
              isNational[bannerKey] = true;
            }
            genrePackages.push(genreToPackageMapping[genre]);
          }
        }
        return {
          genrePackages,
          isNational
        };
      }

      const getIndicators = (category, packageNames: string[]) => {
        if(category?.toUpperCase() === "RSN") {
          return  gridPackages?.map(p => {
            const pkgArr = p?.id.split('TV_');
            const pkgId = pkgArr[pkgArr.length - 1];
            if (packageNames?.some(inP => inP === pkgId)) {
              return PACKAGE_INDICATORS.IN;
            }
            return null;
          })
        }
        return gridPackages?.map(p => {
          const pkgArr = p?.id.split('TV_');
          const pkgId = pkgArr[pkgArr.length - 1];
          if (packageNames?.some(inP => inP.includes(pkgId))) {
            return PACKAGE_INDICATORS.IN;
          }
          return null;
        });
      }
      for (const channel of localChannels) {
      // As of 5/15 hotfix local api doesn't send any package info.
      // Conactenate LOCAL_DEFAULT_PACKAGES packages and genre packages
      // Generated by Copilot- Start - 2026-04-23 09:00:00
      // Per-DMA signature-package exclusion (CMS config: signatureExcludedLocalChannels).
      // Supports both blanket DMA exclusion (all signature pkgs) and per-package narrow rules.
        const channelNetwork = channel?.network || channel?.name || channel?.channelNameLabel;
        const signaturePackages = getSignaturePackagesForLocalChannel(channelNetwork, currentDma, LOCAL_DEFAULT_PACKAGES, signatureExcludedLocalChannels, channel);
        const { genrePackages, isNational } = getGenrePackages(channel);
        const chnlPackages = [
          ...signaturePackages,
          ...(genrePackages?.length ? genrePackages : [])
        ];

        // If a local channel ends up with NO matching packages at all (excluded from
        // every signature package AND not mapped into any genre pack for this DMA),
        // skip injecting it entirely — otherwise it renders as an empty row in the
        // "Included channels" grid (e.g. Telemundo in DMAs 820/635/819/801 when the
        // CMS config blanket-excludes it from signature).
        if (chnlPackages.length === 0) {
          continue;
        }
        // Generated by Copilot- End - 2026-04-23 09:00:00
        const indicators = getIndicators(channel?.categoryId, signaturePackages);
        tempLocalChannels.push({
          ...buildLocalRsnChannel(channel, localrsnUUID++, chnlPackages, indicators),
          chnlPackages,
          displayText: channel?.callSign,
          isNational,
          signatureExcluded: signaturePackages.length === 0
        });
      }
      for (const channel of rsnChannels) {
        const indicators = getIndicators(channel?.categoryId, channel?.packageNames);

        // Read packageNames param from channel object, add genre packages if any
        // Fallback to RSN_DEFAULT_PACKAGES in case of API failure
        const { genrePackages, isNational } = getGenrePackages(channel);
        const chnlPackages = [
          ...(channel?.packageNames?.length ? channel.packageNames : RSN_DEFAULT_PACKAGES),
          ...(genrePackages?.length ? genrePackages : [])
        ]
        tempRsnChannels.push({
          ...buildLocalRsnChannel(channel, localrsnUUID++, chnlPackages, indicators),
          chnlPackages,
          displayText: channel?.callSign,
          isNational
        });
      }

      tempRsnChannels = tempRsnChannels.filter(channel => {
        for (const indicator of channel?.indicators || []) {
          if (indicator === 1) {
            return channel;
          }
        }
        return false;
      })

      // Set state for Icon Banner UI
      setIsLoadingLocalRsn(false);
      // Filter local/rsn channels into the main channel set
      dispatchChlupState({
        type: "ADD_LOCAL_RSN_CHANNELS",
        data: {
          local: tempLocalChannels,
          rsn: tempRsnChannels,
          currentDma: currentDma || nielsenDma,
          genreNationalMapping: genreNationals?.genreNationalMapping,
          signatureExcludedLocalChannels
        }
      })
    }, [gridPackages, genreMapping, genreLocalChannelMapping, genreToPackageMapping]);

    const onCountySearchDone = useCallback(
      (zipcode: string, counties: County[] | null, rsnFee?: string, localRsnChnls?: LocalRsnApiResponse | null, county?: County) => {
        setFetchedZipcodeModuleData(() => ({
          zipcode,
          counties,
          rsnFee,
          localRsnChnls,
          county
        }))
      }
      , []
    );

    /** -------------------------------------------------------------------------------
   * Search and Category Handling
   */
    const updateShowMoreCategories = categories => {
      const data = [];
      categories.forEach((val: any) => data[val] = false);
      setShowMoreCategories(data);
    };

    const updateSearch = (searchChannels: Choice[]) => {
      setSearchChannelList(searchChannels);
    }

    const _multiSelectInputEvents = {
      handleTagsChange: (selectedChannel: Choice[], selectedCategories: KeyValuePair[]) => {
        updateShowMoreCategories(selectedCategories);
        setSelectedCategories(selectedCategories);
        setSearchChannelList(selectedChannel);
      },
      // on category filter
      updateCategories: (categories: KeyValuePair[]) => {
        updateShowMoreCategories(categories?.map(c => c?.key));
        setSelectedCategories(categories);
      },
      // on search item selection
      onSelectSearchListItem: (searchChannels: Choice[]) => {
        setSearchChannelList(searchChannels);
      }
    };

    useEffect(() => {
      setShowMore(selectedCategories?.length > 0);
    }, [selectedCategories]);

    /** -------------------------------------------------------------------------------
   * Model state + Open/Close Events
   */
    const [showChannelModal, setShowChannelModal] = useState(false);
    const [clickedChannelData, setClickedChannelData] = useState(null);
    const [showMore, setShowMore] = useState(false);

    const handleModalClose = () => {
      setShowChannelModal(false);
      setClickedChannelData(null);
    }

    const openChannelModal = channel => {
      setClickedChannelData(() => (channel));
      setShowChannelModal(true);
    };

    return (
    <DtvBox>
      {isSatelliteChlup ? pageHeading?.content && <DtvText
        variant="h2"
        component={pageHeading?.component || "h1"}
        content={pageHeading?.content || "Channel lineups subject to change based on agreements with programming providers"}
        sx={{
          padding: {
 xs: "10px 10px 10px 10px",
sm: "10px 20px 10px 20px",
md: "10px 20px 10px 20px"
},
          backgroundColor: "#ffffff",
          color: colorPalette.black.main,
          textAlign: "center",
          fontSize: {
 xs: "20px",
sm: "32px",
md: "36px"
},
          lineHeight: {
 xs: "28.8px",
sm: "36px",
md: "48.4px"
},
          letterSpacing: {
 xs: "-0.48px",
sm: "-0.6px",
md: "-0.88px"
},
          fontWeight: {
 xs: 400,
sm: 500
},
          height: {
 xs: "80px",
sm: "110px",
md: "130px"
}
        }}
      /> : <></>}
    <DtvBox
      id={`${componentId}-root`}
      sx={{
        background: {
          xs: theming?.pageBackgroundUrl ?
            `url("${theming?.pageBackgroundUrl}?imwidth=300") top / cover no-repeat${theming?.pageBackgroundColor ? `, ${theming?.pageBackgroundColor}` : ''}` :
            theming?.pageBackgroundColor,
          sm: theming?.pageBackgroundUrl ?
            `url("${theming?.pageBackgroundUrl}") top / cover no-repeat${theming?.pageBackgroundColor ? `, ${theming?.pageBackgroundColor}` : ''}` :
            theming?.pageBackgroundColor
        },
        backgroundSize: {
          xs: "100% 221px",
          sm: "100% 434px",
          md: " 100% 810px"
        }
      }}>
        <Head>
          {theming?.pageBackgroundUrl &&
            <link
              rel="preload"
              as="image"
              fetchPriority="high"
              href={theming?.pageBackgroundUrl}
              media="(min-width: 601px)"
            />
          } {theming?.pageBackgroundUrl &&
            <link
              rel="preload"
              as="image"
              fetchPriority="high"
              href={theming?.pageBackgroundUrl}
              media="(max-width: 600px)"
            />
          }
        </Head>

        <DtvBox sx={{
          position: "relative",
          zIndex: zIndexMap.pageHeadingTextContainer,
          padding: {
            xs: "24px 20px 0px 20px",
            sm: "54px 20px 0px 20px",
            md: "90px 20px 0px 20px"
          }
        }}>
          {/** Heading */}
          <DtvText
            variant="h2"
            component={heading?.component || "h1"}
            content={heading?.content || "Stream all your favorite channels"}
            sx={{
              color: theming?.headerTextColor,
              textAlign: "center",
              fontSize: {
 xs: "24px",
sm: "30px",
md: "44px"
},
              lineHeight: {
 xs: "28.8px",
sm: "36px",
md: "48.4px"
},
              letterSpacing: {
 xs: "-0.48px",
sm: "-0.6px",
md: "-0.88px"
},
              fontWeight: {
 xs: 400,
sm: 500
},
              px: {
 xs: "12px",
sm: "32.5px",
md: "50px"
}
            }}
          />
          {/* Toggle Button */}
          {!isSatelliteChlup && (toggleBtn?.enabled) && (
            <DtvBox
              sx={{
                display: "flex",
                justifyContent: "center",
                py: {
 xs: "13px",
sm: "16px",
md: "32px"
},
                width: "100%",
                "& > div": {
                  width: "fit-content",
                  maxWidth: "522px",
                  height: {
 xs: "36px",
sm: "48px",
md: "48px"
}
                },
                "& > div > div": {
                  width: { xs: "100%" }
                },
                "& > div > div > button": {
                  width: "100% !important",
                  height: "100%",
                  fontSize: {
 xs: "14px",
sm: "16px"
},
                  lineHeight: {
 xs: "18.2px",
sm: "20.8px"
},
                  color: colorPalette.neutral_white.main
                }
            }}>
              <TabTray
                key={tabKey > 0 ? tabKey : undefined}
                id="Channel-Category"
                variant="tray"
                tabs={toggleBtn?.toggles}
                defaultSelectedTab={tabDefaultIndex}
                handleChange={handleTabTrayBtnChange}
                ariaLabel={`Show Channel By ${toggleBtn?.toggles?.[curToggleIndex]?.label?.toLowerCase()}`}
                mode={theming?.toggleBtnMode}
                styles={{
                  width: {
 xs: "180px",
md: "250px"
},
                  minHeight: "46px"
                }}
              />
            </DtvBox>
          )}
          {/** Zipcode Module */}
          <DtvBox
            sx={{
              maxWidth: {
                xs: "522px",
                sm: "100%"
              },
              margin: "0 auto",
              paddingBottom: {
                xs: "8px",
                sm: "31px",
                md: "27px"
              }
            }}>
            <ZipcodeModule
              id="Chlup-Zipcode-Module"
              mode={theming?.zipCodeModuleMode}
              onSearchButtonClick={onZipcodeUpdateClick}
              onLocalRsnChnlsConfirmed={buildLocalRsnChannels}
              onCacheConfirmed={onCountySearchDone}
              localRsnApiOptions={localRsnApiOptions}
              ciConfig={zipcodeModuleConfig}
            />
          </DtvBox>
          <DtvBox
            sx={{
              maxWidth: "522px",
              margin: "0 auto"
            }}
          >
            <ChannelSearch
              id={`${componentId}-channel-search`}
              {...searchObj}
              channels={[...searchChoices, ...searchChoicesSvods]}
              theming={theming?.chipsMode}
              updateSearchEvents={updateSearch}
              type="chnlup"
            />
          </DtvBox>
          {/* <MultiChnlSearch
            id={componentId}
            events={multiSelectInputEvents}
            choices={searchChoices?.length ? searchChoices : []}
            choicesSvod={searchChoicesSvods?.length ? searchChoicesSvods: []}
            channelCategories={searchCategories}
            searchConfig={searchConfig}
            genreEnabled={config?.displayGenrePacks}
            initValue={""}
            selectedToggle={selectedToggle}
            theming={theming?.chipsMode}
            isSatelliteChlup={isSatelliteChlup}
          />         */}
          {/* MyFreeDirectv Banner in category tab */}
          {selectedToggle === "CATEGORY" && myFreeDirectvBanner && (
            <DtvBox sx={{
              p: {
                xs: "20px 0px 16px 0px",
                sm: "20px 57.5px 24px 57.5px",
                md: "32px 146px 24px 146px"
              },
              "& > div": {
                pr: {
 xs: "0px !important",
sm: "20px !important"
}
              }
            }}>
              <MyFreeDtvBanner
                {...myFreeDirectvBanner}
                channels={myFreeDtvChannels}
                theming={theming?.categoriesTab?.myFreeDtvBanner}
              />
            </DtvBox>
          )}
          {/* Local Rsn legal + MyFreeDirectv Banner in genre tab */}
          {(selectedToggle === 'GENRE PACKS' && (subheading?.content || subheadingLegal?.content)) && (
            <>
              <DtvBox sx={{
                width: "100%",
                display: "flex",
flexDirection: "column",
justifyContent: "center",
                px: {
                  xs: "14px",
                  sm: "90px",
                  md: "196px"
                }
              }}>
                {subheading?.content && (
                <DtvText
                  component="p"
                  variant="subhead1medium"
                  content={subheading?.content}
                  sx={{
                    color: theming?.subheadingTextColor,
                    textAlign: "center",
                    fontSize: {
 xs: "16px !important",
md: "18px !important"
},
                    fontFamily: 'roboto-regular',
                    fontWeight: '400 !important',
                    mb: {
 xs: "10px",
sm: "12px",
md: "20px"
}
                  }}
                />
                )}
                {/* Local Rsn Legal in genre tab */}
                {subheadingLegal?.content && (
                  <DtvText
                    component="p"
                    variant="smalltext"
                    content={subheadingLegal?.content}
                    sx={{
                      color: theming?.subheadingLegalTextColor,
                      textAlign: "center",
                      fontSize: { xs: "14px !important" },
                      fontFamily: 'roboto-regular',
                      fontWeight: '400 !important'
                    }}
                  />
                )}
              </DtvBox>
              {/* MyFreeDirectvBanner in Genre Tab */}
              {myFreeDirectvBanner && (
              <DtvBox sx={{
                p: {
                  xs: "20px 14px",
                  sm: "27px 90px",
                  md: "36px 196px"
                },
                "& > div": {
                  pr: {
 xs: "0px !important",
sm: "20px !important"
}
                }
              }}>
                <MyFreeDtvBanner
                  {...myFreeDirectvBanner}
                  channels={myFreeDtvChannels}
                  theming={theming?.categoriesTab?.myFreeDtvBanner}
                />
              </DtvBox>
              )}
            </>
          )}
        </DtvBox>

        {(activeView === 'packages' || isSatelliteChlup) ? (
            <PackageComparisonGrid
              channelsAZ={filteredGridChannelsAZ}
              channelsFilteredByNumber={filteredGridChannelsNum}
              channelsCategorical={filteredGridChannelsCategorical}
              myFreeDtvChannels={myFreeDtvChannels}
              categoriesAZ={gridCategoriesAZ}
              categoriesCategorical={gridCategories}
              initViewMode={gridInitViewMode}
              sortOptions={gridSortOptions}
              searchListActive={searchActive}
              searchTerms={searchChannelList}
              packages={gridPackages}
              myFreeDirectvBanner={myFreeDirectvBanner}
              pkgIndicatorIcons={pkgIndicatorIcons}
              pkgIndicatorTooltipPrefixText={pkgIndicatorTooltipPrefixText}
              addOnPromoLogoInfo={addOnPromoLogoInfo}
              theming={theming?.packagesTab}
              noMatchPageMsg={searchConfig?.noResultsMsgPackagesGrid}
              searchErrorMsgPartial={searchConfig?.searchErrorMsgPartial}
              searchErrorMsgFull={searchConfig?.searchErrorMsgFull}
              embeddedSvods={filteredEmbeddedSvods}
              isEmbeddedSvodsEnabled={isEmbeddedSvodsEnabled}
              rsnTierLegalTextObj={content?.rsnTierLegalTextObj}
              myFreeConfig={myFreeConfig}
              isSatelliteChlup={isSatelliteChlup}
            />
        ) : (
          (activeView === 'categories' &&  !isSatelliteChlup) && (
            <DtvBox
              id={`${componentId}-channelsdiv`}
              sx={{
                "::-webkit-scrollbar": {
                  width: "4px"
                },
                "::-webkit-scrollbar-thumb": {
                  backgroundColor: "darkgrey",
                  borderRadius: "2px"
                },
                maxHeight: "100%",
                overflow: "hidden",
                pt: "0px",
mt: {
 xs: "24px",
sm: "24px",
md: "32px"
},
                ...(chlupStore?.channels?.length === searchChannelList.length &&
                    selectedCategories.length === 0 && !showMore
                ) ? {
                  position: "relative",
                  display: "block"
                } : {
                  overflowY: "scroll"
                }
              }}>
                <IconBanner
                  id={ `${componentId}-banner`}
                  bannerCategories={bannerSelectedCategories}
                  bannerChannels={filteredBannerChannels}
                  searchedSvodNames={searchedSvodNames}
              searchedSvods={searchedSvods}
                  searchActive={searchActive}
                  bannerPackages={bannerPackages}
                  isSearching={isLoadingLocalRsn}
                  showMoreData={showMoreCategories}
                  updateShowMoreCategories={data => setShowMoreCategories(data)}
                  openChannelModal={openChannelModal}
                  zipCodeModule={{
                    id: "Chlup-Zipcode-Module-My-Home-Team",
                    mode: theming?.zipCodeModuleMode,
                    size: "small",
                    position: "left",
                    onSearchButtonClick: onZipcodeUpdateClick,
                    onLocalRsnChnlsConfirmed: buildLocalRsnChannels,
                    localRsnApiOptions,
                    initData: fetchedZipcodeModuleData,
                    isConditional: true
                  }}
                  categoryFooterCtas={categoryFooterCtas}
                  localChnlBadgeTooltip={localChnlBadgeTooltip}
                  rsnChnlBadgeTooltip={rsnChnlBadgeTooltip}
                  subscriptionsLabel={subscriptionsLabel}
                  channelsLabel={channelsLabel}
                  internetChnlsPDFLink={selectedToggle === "CATEGORY" ? internetChnlsPDFLink : undefined}
                  theming={theming?.categoriesTab?.category}
                  noMatchPageMsg={searchConfig?.noResultsMsgCategoriesBanner}
                  genreNationals={genreNationals}
                  myFreeConfig={myFreeConfig}
                />
            </DtvBox>
          )
        )}

        {(activeView === 'packages' || isSatelliteChlup) && (
          <ChnlListingPageFooter
            id={`${componentId}-Footer`}
            internetChnlsPDFLink={internetChnlsPDFLink}
            satelliteChnlsPDFLink={satelliteChnlsPDFLink}
            isSatelliteChlup={isSatelliteChlup}
            satelliteCta={satelliteCta}
            theme={theming?.footerMode}
          />
        )}

        <ScrollToTop
          showAlways={false}
          backgroundColor="#FFF"
          color="#102641"
        />

        <DtvModal
          open={showChannelModal}
          handleClose={handleModalClose}
          modalStyle={{
            maxHeight: 'min-content'
          }}
          modalBodyStyle={{
            height: 'min-content',
            maxHeight: {
              xs: '90vh',
              sm: '80vh'
            }
          }}>
            <>
            {['rsn', 'local'].includes(clickedChannelData?.category?.toLowerCase()) ?
              <RsnChannelModal
                  channel={clickedChannelData}
                  packages={bannerPackages}
                  ctaForInternetInLocalRSN={channelModalContent?.ctaForInternetInLocalRSN}
                  ctaForSatelliteLocalRSN={channelModalContent?.ctaForSatelliteLocalRSN}
                  chnlSrcLC={chnlSrcLC}
                  rsnTiers={channelModalContent?.rsnTiers}
                  // legacy
                  rsnModalLegal={channelModalContent?.rsnModalLegal}
                /> :
              <ChannelModal
                  channel={clickedChannelData}
                  packages={bannerPackages}
                  internetPkgCta={channelModalContent?.internetPkgCta}
                  satellitePkgCta={channelModalContent?.satellitePkgCta}
                  myFreeDTVCTA={channelModalContent?.myFreeDTVCTA}
                  myFreeDTVPkgLabel={channelModalContent?.myFreeDTVPkgLabel}
                  chnlSrcLC={chnlSrcLC}
                />
            }
            </>
        </DtvModal>
    </DtvBox>
    </DtvBox>
    );
  } catch (error) {
    console.error("[ChannelListingPage] !!!!! FATAL ERROR !!!!! ", error);
    return <ErrorDisplay error={error} component="Channel Listing Page"/>;
  }
}
